// src/components/ResetPassword.tsx
import React, { useState } from 'react';
import { Form, Button, Alert, Col, Card, Row, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useUpdatePassword from '../../hooks/useUpdatePassword';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const { updatePassword } = useUpdatePassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.passwordsDoNotMatch'));
      setTimeout(() => setError(null), 3000);
      return;
    }
    const updatePasswordData = { currentPassword: currentPassword, newPassword: newPassword };
    const result = await updatePassword(updatePasswordData);
    // Add logic to reset password here
    if (result.success) {
      setSuccess(true);
      setError(null);
      setTimeout(() => setSuccess(false), 4000);
    } else {
      setSuccess(false);
      if (result.message && result.message.includes('6')) {
        result.message = t("resetPassword.passwordTooShort");
      } else if (result.message && result.message.includes('PasswordRequiresUpper')) {
        result.message = t("resetPassword.passwordRequiresUpper");
      } else if (result.message && result.message.includes('PasswordMismatch')) {
        result.message = t("resetPassword.passwordMismatch");
      } else if (result.message === ('tooManyRequests')) {
        result.message = t("tooManyRequests");
      };
      setError(result.message);
      setTimeout(() => setError(null), 4000);
    }
  };

  return (
    <Card className="bg-transparent border-0">
      <Card.Header className='border-0 bg-transparent'>
        <Card.Title className="text-secondary text-center fs-1 fw-bold m-3">{t('resetPassword.title')}</Card.Title>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{t('resetPassword.success')}</Alert>}
          <Row className="mb-4 d-flex justify-content-center">
            <Col md={6}>
              <Form.Group className="mb-0 text-nowrap">
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('resetPassword.currentPassword')}</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2'
                  />
                  <Button variant="secondary" className="align-items-right" onClick={() => setShowCurrentPassword(!showCurrentPassword)}><FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} /></Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mt-4 mt-md-0 mb-0 text-nowrap">
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('resetPassword.newPassword')}</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2'
                  />
                  <Button variant="secondary" className="align-items-right" onClick={() => setShowNewPassword(!showNewPassword)}><FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} /></Button>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4 d-flex justify-content-center">
            <Col md={6}>
              <Form.Group className="mb-0 text-nowrap">
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('resetPassword.confirmPassword')}</Form.Label>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2'
                />
              </Form.Group>
            </Col>
          </Row>
          <Col className="d-flex justify-content-center">
            <Button className="py-2 px-3 fs-5 rounded-3" variant="secondary" type="submit">
              {t('resetPassword.button')}
            </Button>
          </Col>
        </Form>
      </Card.Body>
    </Card >
  );
};

export default ResetPassword;
